/**
 * The activity register — who did what and when, in one place (bata-ui#218, `docs/adr/9037`).
 * The pure half: the scope on the link, the read's query, the words a row is drawn with, the
 * conclusion, and the CSV. The screen is `screens/ActivityScreen.tsx`; the contract is
 * `./activity-contract.ts`.
 *
 * ══ THE SCOPE IS ON THE LINK, AND THE PERIOD HAS NO DEFAULT ══════════════════
 *
 * `GET /foundations/activity` requires `from` and `to` (inclusive, a window the platform bounds).
 * DEC-85 and CLAUDE.md §3.4: no default, ever. "The last 7 days" chosen here on the planner's
 * behalf would be a read over a window nobody asked for, and a shared link would answer a
 * different question every day. With no period the page asks for one; the quick picks WRITE the
 * dates to the link, so the view that follows names its window.
 *
 *   ?from=YYYY-MM-DD&to=YYYY-MM-DD      the window, both required
 *   ?actor=  ?app=  ?action=  ?country=  ?object=      the filters, each optional
 *
 * `?app=` and `?country=` are the spellings the parameter register and Data already use for the
 * same two things (an app, a market); `?object=` is the object kind.
 *
 * ══ NOTHING IS INVENTED ══════════════════════════════════════════════════════
 *
 * A machine's action reads «System», never a name. An action whose source does not record who
 * took it reads «—», and why is said once under the table (ADR 9028). The conclusion counts only
 * the rows drawn; while the cursor has more it says the rest exist rather than extrapolating.
 *
 * ══ FIFTY, NEWEST FIRST; THE CSV IS THE WHOLE FILTERED SET (ADR 9034) ════════
 *
 * The table draws one page of `ACTIVITY_TABLE_LIMIT` (fifty) rows: the read is newest first
 * (bata-platform ADR 2009 §5), so they are the fifty most recent of the window after the
 * planner's filters. There is no «Carica altre»: the rest is reached by narrowing the filters,
 * and the CSV follows the cursor through every page of the same filtered read.
 */

import { MAX_PAGES, callOperation, reportPageRead } from '@/api';
import {
  NOT_PUBLISHED, NO_PRINCIPAL, buildVersion,
  type ExportCell, type ExportColumn, type ExportRequest, type ExportTranslate, type ProvenanceFilter,
} from '@/export';
import { TABLE_ROW_CAP, personAbsentReason, type PersonReason } from '@/planning';

import type { ExportScope } from '../manifest';

import {
  ACTIVITY_ACTIONS, ACTIVITY_APPS, ACTIVITY_MAX_LIMIT, ACTIVITY_READ,
  type ActivityAction, type ActivityApp, type ActivityEntry, type ActivityPage, type ActivityQuery, type ActivityRead, type ActivitySource,
} from './activity-contract';
import { COUNTRY_PARAM, readCountry } from './data-schedules';

// ── the link ─────────────────────────────────────────────────────────────────

export const ACTIVITY_ROUTE = '/foundations/activity';
export const FROM_PARAM = 'from';
export const TO_PARAM = 'to';
export const ACTOR_PARAM = 'actor';
export const ACTIVITY_APP_PARAM = 'app';
export const ACTION_PARAM = 'action';
export const OBJECT_PARAM = 'object';
export { COUNTRY_PARAM };

/** Every key the page writes — what a "clear the filters" leaves alone is the period. */
export const ACTIVITY_FILTER_PARAMS = [ACTOR_PARAM, ACTIVITY_APP_PARAM, ACTION_PARAM, COUNTRY_PARAM, OBJECT_PARAM] as const;
export type ActivityFilterParam = (typeof ACTIVITY_FILTER_PARAMS)[number];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface ActivityPeriod {
  readonly from: string;
  readonly to: string;
}

export interface ActivityFilters {
  readonly actor: string | null;
  readonly app: ActivityApp | null;
  readonly action: ActivityAction | null;
  readonly country: string | null;
  readonly objectKind: string | null;
}

export interface ActivityScope {
  /** Both dates, or `null` — one date alone is not a window. */
  readonly period: ActivityPeriod | null;
  readonly filters: ActivityFilters;
}

function param(params: URLSearchParams, name: string): string | null {
  const value = params.get(name)?.trim() ?? '';
  return value === '' ? null : value;
}

/** A real calendar day, not just the shape of one — `2026-02-31` is not a date. */
export function isCalendarDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function isActivityApp(value: string): value is ActivityApp {
  return (ACTIVITY_APPS as readonly string[]).includes(value);
}

/**
 * The scope a search string states. Pure: a string in, never `window`, no default. A malformed
 * date is absent — the page asks for the period again rather than sending a typo. The ORDER of
 * the two dates and the window's length are the platform's to judge: its refusal is shown.
 */
export function readActivityScope(search: string): ActivityScope {
  const params = new URLSearchParams(search);
  const from = param(params, FROM_PARAM);
  const to = param(params, TO_PARAM);
  const app = param(params, ACTIVITY_APP_PARAM);
  const action = param(params, ACTION_PARAM);
  return {
    period: from !== null && to !== null && isCalendarDate(from) && isCalendarDate(to) ? { from, to } : null,
    filters: {
      actor: param(params, ACTOR_PARAM),
      app: app !== null && isActivityApp(app) ? app : null,
      action: action !== null && isActivityAction(action) ? action : null,
      country: readCountry(search),
      objectKind: param(params, OBJECT_PARAM),
    },
  };
}

/**
 * The rows the table draws: ONE page of fifty, the most recent of the filtered window (ADR 9034's
 * cap, `TABLE_ROW_CAP`; the read is newest first). Asked of the platform as `limit`, so the page
 * never reads more rows than it may draw.
 */
export const ACTIVITY_TABLE_LIMIT = TABLE_ROW_CAP;

/**
 * The read's query for a scope, or `null` while the period is missing — nothing is sent. `limit`
 * is the page size: `ACTIVITY_TABLE_LIMIT` for the table, `ACTIVITY_MAX_LIMIT` for the CSV's walk.
 */
export function activityQuery(scope: ActivityScope, limit?: number): ActivityQuery | null {
  if (scope.period === null) return null;
  const { actor, app, action, country, objectKind } = scope.filters;
  return {
    from: scope.period.from,
    to: scope.period.to,
    ...(limit === undefined ? {} : { limit }),
    ...(actor === null ? {} : { actor }),
    ...(app === null ? {} : { app }),
    ...(action === null ? {} : { action }),
    ...(country === null ? {} : { country }),
    ...(objectKind === null ? {} : { object_kind: objectKind }),
  };
}

/** The quick picks — «Ultimi 7 giorni», «Ultimi 30 giorni». */
export const QUICK_PERIOD_DAYS = [7, 30] as const;

/**
 * The last `days` calendar days, today included, in UTC — the calendar the platform's
 * timestamps are in. `now` is an argument so the test fixes the clock.
 */
export function lastDays(days: number, now: number): ActivityPeriod {
  const today = new Date(now);
  const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - (days - 1)));
  return { from: start.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) };
}

/**
 * Write several of the page's keys onto the link in ONE history entry, keeping everything else.
 * `null` removes a key. `pushState` plus a manual `popstate`, as `setFoundationsScopeParam` does
 * and for the same reason: the shell's navigation fires no event of its own.
 */
export function writeActivityParams(updates: Readonly<Record<string, string | null>>): void {
  if (typeof window === 'undefined') return;
  const next = new URLSearchParams(window.location.search);
  for (const [name, value] of Object.entries(updates)) {
    if (value === null || value.trim() === '') next.delete(name);
    else next.set(name, value);
  }
  const query = next.toString();
  window.history.pushState({}, '', query === '' ? window.location.pathname : `?${query}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// ── the words of a row ───────────────────────────────────────────────────────

/** Each app's name — spelled out, so the catalogue check sees every key. */
export const ACTIVITY_APP_LABEL_KEY: Readonly<Record<ActivityApp, string>> = {
  pricing: 'foundations.activity.app.pricing',
  replenishment: 'foundations.activity.app.replenishment',
  compacting: 'foundations.activity.app.compacting',
  management: 'foundations.activity.app.management',
  foundations: 'foundations.activity.app.foundations',
};

/**
 * Each action of #482's fixed vocabulary in words — spelled out, so the catalogue check sees every
 * key, and typed over `ActivityAction` so a word added to the contract without words here does not
 * compile. A word outside the vocabulary is shown as the platform spells it: a translation guessed
 * for a word nobody defined would be a claim about what happened.
 */
export const ACTIVITY_ACTION_LABEL_KEY: Readonly<Record<ActivityAction, string>> = {
  proposed: 'foundations.activity.action.proposed',
  approved: 'foundations.activity.action.approved',
  rejected: 'foundations.activity.action.rejected',
  adjusted: 'foundations.activity.action.adjusted',
  deferred: 'foundations.activity.action.deferred',
  exported: 'foundations.activity.action.exported',
  confirmed: 'foundations.activity.action.confirmed',
  assigned: 'foundations.activity.action.assigned',
  snoozed: 'foundations.activity.action.snoozed',
  reopened: 'foundations.activity.action.reopened',
  simulated: 'foundations.activity.action.simulated',
  adopted: 'foundations.activity.action.adopted',
  reversed: 'foundations.activity.action.reversed',
  withdrawn: 'foundations.activity.action.withdrawn',
  accepted: 'foundations.activity.action.accepted',
  raised: 'foundations.activity.action.raised',
  unassigned: 'foundations.activity.action.unassigned',
};

export function isActivityAction(value: string): value is ActivityAction {
  return (ACTIVITY_ACTIONS as readonly string[]).includes(value);
}

/** The catalogue key for an action, or `null` for a word outside the vocabulary (shown raw). */
export function actionLabelKey(action: string): string | null {
  return isActivityAction(action) ? ACTIVITY_ACTION_LABEL_KEY[action] : null;
}

/**
 * Each object kind the register names in words (bata-platform `repositories/activity.py`, the
 * `OBJECT_*` constants). The contract types the kind as a free string, so a kind this table does
 * not name is shown as the platform spells it — never borrowed words.
 */
export const ACTIVITY_OBJECT_KIND_LABEL_KEY: Readonly<Record<string, string>> = {
  run: 'foundations.activity.objectKind.run',
  cadence_bulk_action: 'foundations.activity.objectKind.cadenceBulkAction',
  consolidation_action: 'foundations.activity.objectKind.consolidationAction',
  change_set: 'foundations.activity.objectKind.changeSet',
  alert: 'foundations.activity.objectKind.alert',
  alert_group: 'foundations.activity.objectKind.alertGroup',
  write_back_command: 'foundations.activity.objectKind.writeBackCommand',
  write_back_group: 'foundations.activity.objectKind.writeBackGroup',
  simulation: 'foundations.activity.objectKind.simulation',
  action: 'foundations.activity.objectKind.action',
};

/** The catalogue key for an object kind, or `null` for a kind this build does not name (shown raw). */
export function objectKindLabelKey(kind: string): string | null {
  return ACTIVITY_OBJECT_KIND_LABEL_KEY[kind] ?? null;
}

/** Who acted, as the page may say it. */
export type ActorWords =
  | { readonly kind: 'machine'; readonly id: string | null }
  /**
   * `named: false` — no name is on record for this person, so `name` IS the principal id (drawn in
   * the mono face, as an identifier). The platform publishes `actorName` from the user's profile
   * (DEC-97); a person with no profile has none.
   */
  | { readonly kind: 'person'; readonly name: string; readonly id: string | null; readonly named: boolean }
  /**
   * No name, and the read says why (`actorNameAbsentReason`, ADR 2127): a service account or a person
   * with no name on their profile. Said in words; the id goes behind the «id» button.
   */
  | { readonly kind: 'reasoned'; readonly reason: PersonReason; readonly id: string }
  | { readonly kind: 'unrecorded' };

/**
 * «Sistema» for a machine — never its id as if it were a name; the person's name, else their id;
 * and for an action whose source records nobody, `unrecorded` — «—», its reason said once.
 */
export function actorWords(
  entry: Pick<ActivityEntry, 'actor' | 'actorName' | 'actorIsMachine'> & { readonly actorNameAbsentReason?: string | null | undefined },
): ActorWords {
  if (entry.actorIsMachine) return { kind: 'machine', id: entry.actor };
  const published = entry.actorName?.trim() ?? '';
  if (published !== '') return { kind: 'person', name: published, id: entry.actor, named: true };
  const id = entry.actor?.trim() ?? '';
  const reason = personAbsentReason(entry.actorNameAbsentReason);
  if (id !== '' && reason === 'machine') return { kind: 'machine', id };
  if (id !== '' && (reason === 'service_account' || reason === 'no_profile')) return { kind: 'reasoned', reason, id };
  return id === '' ? { kind: 'unrecorded' } : { kind: 'person', name: id, id: entry.actor, named: false };
}

/**
 * Who approved, as the page may say it: the name on record, else the principal id (`named: false`,
 * drawn in mono and covered by the same "no name on record" line as «Chi»), else nothing.
 */
export function approverWords(
  entry: Pick<ActivityEntry, 'approvedBy' | 'approvedByName'> & { readonly approvedByNameAbsentReason?: string | null | undefined },
): { readonly name: string; readonly named: boolean; readonly reason?: PersonReason } | null {
  const published = entry.approvedByName?.trim() ?? '';
  if (published !== '') return { name: published, named: true };
  const id = entry.approvedBy?.trim() ?? '';
  if (id === '') return null;
  // ADR 2127: the read says why there is no name — said in words, the id behind the «id» button.
  const reason = personAbsentReason(entry.approvedByNameAbsentReason);
  return reason === null || reason === 'machine' ? { name: id, named: false } : { name: id, named: false, reason };
}

/** Whether a row shows an id where a person's name would stand — in «Chi» or «Approvato da». */
export function showsIdForName(entry: ActivityEntry): boolean {
  const who = actorWords(entry);
  const approver = approverWords(entry);
  return (who.kind === 'person' && !who.named) || (approver?.named === false && approver.reason === undefined);
}

/** Whether the platform published the object's label — otherwise the page shows its key. */
export function hasObjectLabel(entry: Pick<ActivityEntry, 'objectLabel'>): boolean {
  return (entry.objectLabel?.trim() ?? '') !== '';
}

/**
 * The object as a person reads it: its label, else its key — never the word «null» (ADR 0014).
 * The platform labels one kind so far: a write-back group act over one store carries the store
 * (bata-platform ADR 2009 amendment of 2026-10-04, issue 249); every other row is its key, and
 * the notes under the table say why once.
 */
export function objectWords(entry: Pick<ActivityEntry, 'objectLabel' | 'objectKey'>): string {
  return hasObjectLabel(entry) ? (entry.objectLabel ?? '').trim() : entry.objectKey;
}

/** The reason: its label, else its code, else nothing recorded. */
export function reasonWords(entry: Pick<ActivityEntry, 'reasonLabel' | 'reasonCode'>): string | null {
  const label = entry.reasonLabel?.trim() ?? '';
  if (label !== '') return label;
  const code = entry.reasonCode?.trim() ?? '';
  return code === '' ? null : code;
}

/**
 * The note the actor wrote on the act, verbatim (bata-platform PR 545, v0.69.0; issue 249), or
 * `null` when none was written or the source keeps none. Never translated and never shortened
 * here: it is the person's own words. Blank and whitespace-only notes are no note.
 */
export function commentaryWords(entry: Pick<ActivityEntry, 'commentary'>): string | null {
  const note = entry.commentary?.trim() ?? '';
  return note === '' ? null : note;
}

/**
 * The object's page, only when it is a path inside this application. A link is data from the
 * register, and an absolute or `javascript:` URL drawn as an in-app anchor would send a click
 * wherever a record said — so anything that is not a plain `/path` is not a link here.
 */
export function inAppLink(link: string | null): string | null {
  if (link === null) return null;
  const trimmed = link.trim();
  return /^\/(?![/\\])/.test(trimmed) ? trimmed : null;
}

/**
 * The sources the notes under the table speak of, each once, in the platform's order (brief part 3
 * A3 note 2): a source the register does not cover yet, and a covered one whose retention is
 * limited — the platform says which with `note` (#482: `table_absent`,
 * `newest_simulations_per_stage_and_market_only`, `purged_with_consolidation_run_after_retention`).
 */
export function sourceNotes(pages: readonly Pick<ActivityPage, 'sources'>[]): readonly ActivitySource[] {
  const seen = new Set<string>();
  const out: ActivitySource[] = [];
  for (const page of pages) {
    for (const source of page.sources) {
      const limited = source.note !== null && source.note.trim() !== '';
      if ((source.covered && !limited) || seen.has(source.source)) continue;
      seen.add(source.source);
      out.push(source);
    }
  }
  return out;
}

/**
 * #482's `sources[].note` codes in words — spelled out, so the catalogue check sees every key. A
 * code this build does not know is shown as the platform spells it.
 */
export const SOURCE_NOTE_KEY: Readonly<Record<string, string>> = {
  table_absent: 'foundations.activity.sourceNote.tableAbsent',
  newest_simulations_per_stage_and_market_only: 'foundations.activity.sourceNote.newestSimulations',
  purged_with_consolidation_run_after_retention: 'foundations.activity.sourceNote.purgedWithRun',
};

// ── the day rows ─────────────────────────────────────────────────────────────

/** The first row of a UTC day on the page, with that day and how many of the rows drawn fall in it. */
export interface DayHead {
  /** `YYYY-MM-DD`, UTC. */
  readonly day: string;
  readonly count: number;
}

/**
 * Where a day row goes (brief part 3 A3): before the first action of each UTC day, counting the
 * actions DRAWN on that day — «N azioni caricate», never the day's total. Rendering only: the table
 * stays one row per action and the CSV never carries a day row. A row whose instant does not parse
 * opens no day.
 */
export function dayHeads(rows: readonly Pick<ActivityEntry, 'activityId' | 'occurredAt'>[]): ReadonlyMap<string, DayHead> {
  const heads = new Map<string, DayHead>();
  let open: { readonly id: string; readonly day: string; count: number } | null = null;
  const close = (): void => {
    if (open !== null) heads.set(open.id, { day: open.day, count: open.count });
  };
  for (const row of rows) {
    const ms = Date.parse(row.occurredAt);
    const day = Number.isNaN(ms) ? null : new Date(ms).toISOString().slice(0, 10);
    if (day === null) continue;
    if (open !== null && open.day === day) {
      open.count += 1;
      continue;
    }
    close();
    open = { id: row.activityId, day, count: 1 };
  }
  close();
  return heads;
}

// ── the conclusion ───────────────────────────────────────────────────────────

export interface ActivityFigures {
  /** Rows drawn. */
  readonly actions: number;
  /** Distinct PEOPLE among them — machines and unrecorded actors are not users. */
  readonly users: number;
  /** Of them, the approvals (`approved`) — the figure the brief's filtered sentence names. */
  readonly approvals: number;
}

/** Figures over the rows drawn — the only rows the page may speak for. */
export function activityFigures(rows: readonly ActivityEntry[]): ActivityFigures {
  const users = new Set<string>();
  let approvals = 0;
  for (const row of rows) {
    const who = actorWords(row);
    if (who.kind === 'person') users.add(who.id ?? who.name);
    // A person with no name on their profile is still a user; a service account is not one.
    if (who.kind === 'reasoned' && who.reason === 'no_profile') users.add(who.id);
    if (row.action === 'approved') approvals += 1;
  }
  return { actions: rows.length, users: users.size, approvals };
}

/** The period's length in calendar days, both ends included — read off the link, not the rows. */
export function periodDays(period: ActivityPeriod): number {
  const from = Date.parse(`${period.from}T00:00:00Z`);
  const to = Date.parse(`${period.to}T00:00:00Z`);
  return Math.round((to - from) / 86_400_000) + 1;
}

/** Whether any filter is on the link. */
export function hasFilters(filters: ActivityFilters): boolean {
  return Object.values(filters).some((value) => value !== null);
}

export interface ActivitySentence {
  readonly key: string;
  readonly values: Readonly<Record<string, string | number>>;
}

/**
 * The page's sentence (brief part 3 A2), only with numbers computed from the rows drawn:
 *
 *   no row, no filter     «Nessuna azione registrata dal {from} al {to}»
 *   no row, a filter      «Nessuna azione con questi filtri»
 *   a user filter         «{utente}: {N} azioni in {giorni} giorni, {K} approvazioni»
 *   otherwise             «{N} azioni in {giorni} giorni, di {M} utenti»
 *
 * While the cursor has more, the line under it says so (`foundations.activity.partial`, ADR 9034):
 * the count is of the fifty most recent. How many more is not said — the read publishes no total.
 */
export function activitySentence(
  figures: ActivityFigures,
  scope: ActivityScope & { readonly period: ActivityPeriod },
  words: { readonly from: string; readonly to: string; readonly actor: string | null },
): ActivitySentence {
  const days = periodDays(scope.period);
  if (figures.actions === 0) {
    return hasFilters(scope.filters)
      ? { key: 'foundations.activity.sentence.noneFiltered', values: {} }
      : { key: 'foundations.activity.sentence.none', values: { from: words.from, to: words.to } };
  }
  if (scope.filters.actor !== null) {
    return {
      key: 'foundations.activity.sentence.actor',
      values: { actor: words.actor ?? scope.filters.actor, count: figures.actions, days, approvals: figures.approvals },
    };
  }
  return { key: 'foundations.activity.sentence.complete', values: { count: figures.actions, days, users: figures.users } };
}

// ── the CSV ──────────────────────────────────────────────────────────────────

export const ACTIVITY_EXPORT_ID = 'foundations.activity.table';

const PLATFORM_VERSION = buildVersion();

/** Blank for a field the row does not carry — the register's own null, not a measured zero. */
const text = (value: string | null): ExportCell => ({ kind: 'text', value: value ?? '' });

/** The filters on the link, as the provenance sheet lists them. */
export function activityFilters(scope: ActivityScope): readonly ProvenanceFilter[] {
  const { actor, app, action, country, objectKind } = scope.filters;
  const pairs: readonly (readonly [string, string | null])[] = [
    ['actor', actor], ['app', app], ['action', action], ['country', country], ['object_kind', objectKind],
  ];
  return pairs.flatMap(([field, value]) => (value === null ? [] : [{ field, operator: '=', value }]));
}

/** A code in words through `key`, in the workbook only; a code without words stays as it is. */
const wordsOf = (key: (code: string) => string | null) => (code: string, translate: ExportTranslate): string => {
  const found = key(code);
  if (found === null) return code;
  const text = translate(found);
  return text === '' || text === found ? code : text;
};

/**
 * The register's columns. The CSV keeps every code (`true`, `pricing`, `approved`, `alert_group`);
 * the Excel workbook shows the words the page shows (DEC-104, `docs/adr/9041`): «Sì»/«No» for a
 * machine, the app's and the action's names, the object kind's name.
 */
export const ACTIVITY_EXPORT_COLUMNS: readonly ExportColumn[] = [
  { key: 'occurred_at', headerKey: 'foundations.activity.column.whenLocal', kind: 'date', descriptionKey: 'foundations.columnGuide.activity.occurredAt' },
  { key: 'actor', headerKey: 'foundations.activity.column.actor', kind: 'text', descriptionKey: 'foundations.columnGuide.activity.actor' },
  { key: 'actor_name', headerKey: 'foundations.activity.column.actorName', kind: 'text', descriptionKey: 'foundations.columnGuide.activity.actorName' },
  {
    key: 'actor_is_machine', headerKey: 'foundations.activity.column.actorIsMachine', kind: 'label',
    display: wordsOf((code) => (code === 'true' ? 'export.boolean.yes' : code === 'false' ? 'export.boolean.no' : null)),
    descriptionKey: 'foundations.columnGuide.activity.actorIsMachine',
  },
  {
    key: 'app', headerKey: 'foundations.activity.column.app', kind: 'label',
    display: wordsOf((code) => (isActivityApp(code) ? ACTIVITY_APP_LABEL_KEY[code] : null)),
    descriptionKey: 'foundations.columnGuide.activity.app',
  },
  { key: 'action', headerKey: 'foundations.activity.column.action', kind: 'label', display: wordsOf(actionLabelKey), descriptionKey: 'foundations.columnGuide.activity.action' },
  { key: 'object_kind', headerKey: 'foundations.activity.column.objectKind', kind: 'text', display: wordsOf(objectKindLabelKey), descriptionKey: 'foundations.columnGuide.activity.objectKind' },
  { key: 'object_key', headerKey: 'foundations.activity.column.objectKey', kind: 'text', descriptionKey: 'foundations.columnGuide.activity.objectKey' },
  { key: 'object_label', headerKey: 'foundations.activity.column.object', kind: 'text', descriptionKey: 'foundations.columnGuide.activity.objectLabel' },
  { key: 'country', headerKey: 'foundations.activity.column.market', kind: 'text', descriptionKey: 'foundations.columnGuide.activity.country' },
  { key: 'reason_code', headerKey: 'foundations.activity.column.reasonCode', kind: 'text', descriptionKey: 'foundations.columnGuide.activity.reasonCode' },
  { key: 'reason_label', headerKey: 'foundations.activity.column.reason', kind: 'text', descriptionKey: 'foundations.columnGuide.activity.reasonLabel' },
  { key: 'commentary', headerKey: 'foundations.activity.column.commentary', kind: 'text', descriptionKey: 'foundations.columnGuide.activity.commentary' },
  { key: 'approved_by',headerKey: 'foundations.activity.column.approvedBy', kind: 'text', descriptionKey: 'foundations.columnGuide.activity.approvedBy' },
  { key: 'approved_by_name', headerKey: 'foundations.activity.column.approvedByName', kind: 'text', descriptionKey: 'foundations.columnGuide.activity.approvedByName' },
  { key: 'approved_at', headerKey: 'foundations.activity.column.approvedAt', kind: 'date', descriptionKey: 'foundations.columnGuide.activity.approvedAt' },
  { key: 'source', headerKey: 'foundations.activity.column.source', kind: 'text', descriptionKey: 'foundations.columnGuide.activity.source' },
];

/**
 * The register's sheet over the rows given — every page of the filtered read (`readAllActivity`),
 * for the table's download and the route's alike (ADR 9034: the CSV is the filtered set). Every field the platform publishes leaves raw: codes, not
 * labels, and ISO instants. An actor the source did not record leaves `not-reported`, never blank
 * and never a name.
 */
export function activityRequest(rows: readonly ActivityEntry[], scope: ActivityScope): ExportRequest {
  const cells: ExportCell[][] = rows.map((row) => [
    { kind: 'date', iso: row.occurredAt },
    row.actor === null && !row.actorIsMachine ? { kind: 'missing', state: 'not-reported' } : text(row.actor),
    text(row.actorName),
    { kind: 'label', value: String(row.actorIsMachine), code: String(row.actorIsMachine) },
    { kind: 'label', value: row.app, code: row.app },
    { kind: 'label', value: row.action, code: row.action },
    text(row.objectKind),
    text(row.objectKey),
    text(row.objectLabel),
    text(row.country),
    text(row.reasonCode),
    text(row.reasonLabel),
    text(commentaryWords(row)),
    text(row.approvedBy),
    text(row.approvedByName),
    row.approvedAt === null ? text(null) : { kind: 'date', iso: row.approvedAt },
    text(row.source),
  ]);
  const filters = activityFilters(scope);
  return {
    id: ACTIVITY_EXPORT_ID,
    titleKey: 'foundations.activity.caption',
    columns: ACTIVITY_EXPORT_COLUMNS,
    rows: () => cells,
    rowCount: cells.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: scope.period === null ? NOT_PUBLISHED : { from: scope.period.from, to: scope.period.to },
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{
        measure: 'activity_entry',
        definition: 'One recorded action: who took it (or that a machine did), when, in which app, on which '
          + 'object, with its reason, the note its actor wrote and its approval, as GET /foundations/activity publishes it.',
        registerVersion: '',
      }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: PLATFORM_VERSION,
    },
    perimeter: {
      filters,
      sort: [],
      visibleColumns: ACTIVITY_EXPORT_COLUMNS.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * Every page of one read, following `nextCursor` to the end — bounded as the transport's own walk
 * is (`MAX_PAGES`), and failing loudly past it rather than leaving part of the window out. Pages of
 * `ACTIVITY_MAX_LIMIT`, whatever page size the query came with: the walk is the CSV's, not the
 * table's fifty.
 */
export async function readAllActivity(read: ActivityRead, query: ActivityQuery): Promise<readonly ActivityEntry[]> {
  const rows: ActivityEntry[] = [];
  const walk: ActivityQuery = { ...query, limit: ACTIVITY_MAX_LIMIT };
  let cursor: string | null = null;
  let pages = 0;
  do {
    const page: ActivityPage = await callOperation(read, { query: cursor === null ? walk : { ...walk, cursor } });
    rows.push(...page.items);
    // The file's walk: the download's progress counts its pages (`docs/adr/9074`).
    reportPageRead(page.items.length);
    cursor = page.nextCursor;
    pages += 1;
    if (pages > MAX_PAGES && cursor !== null) {
      throw new Error(`activity export: more than ${MAX_PAGES} pages for ${query.from}–${query.to}; narrow the period or the filters`);
    }
  } while (cursor !== null);
  return rows;
}

/**
 * The route's export (G2 test d): the same read with the same period and filters as the link,
 * every page. Refuses with no period (the page reads nothing then, and neither does its file) and
 * without a read — a file of no rows would say "nothing happened".
 */
export async function activityExport(scope: ExportScope, read: ActivityRead | null = ACTIVITY_READ): Promise<ExportRequest> {
  if (read === null) throw new Error('activity export: the pinned client has no GET /foundations/activity yet (bata-platform issue 482)');
  const activity = readActivityScope(scope.search);
  const query = activityQuery(activity);
  if (query === null) throw new Error('activity export requires a period (?from=&to=); none is on the link');
  return activityRequest(await readAllActivity(read, query), activity);
}
